import {
  AppShell,
  Blockquote,
  Button,
  FileInput,
  Flex,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { RootState } from "../store";
import { useAppDispatch, useAppSelector } from "../hooks";
import { initialize } from "../slices/form/uploadGuests";
import {
  displayInfoMessage,
  displayServerErrorMessage,
} from "../shared/ErrorMessage";
import {
  UploadGuestsRequest,
  useUploadGuestsMutation,
} from "../slices/api/guestsApi";
import { IconInfo, IconUpload } from "../shared/components/Icons";
interface Props {
  ventId: string;
  ventName: string;
  onClose: () => void;
}
function UploadGuests(props: Props) {
  const { data } = useAppSelector((state: RootState) => state.uploadGuestsForm);
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data) return; // prevent reset when re-mounting component
    dispatch(
      initialize({
        fileName: "",
      }),
    );
  }, []);
  const [uploadGuests] = useUploadGuestsMutation();
  const [selectedFile, setSelectedFile] = useState<File | null>();

  const handleUploadGuests = async () => {
    if (data == null || selectedFile == null) {
      console.error("undefined");
      return;
    }

    setIsUploading(true);
    try {
      const request: UploadGuestsRequest = {
        file: selectedFile,
        ventId: props.ventId,
      };
      const response = await uploadGuests(request).unwrap();
      if (response.duplicateEmails?.length ?? 0 > 0) {
        const dups = response.duplicateEmails.join(", ");
        displayInfoMessage(
          `${response?.guests?.length ?? 0} guests were created. However, ${response.duplicateEmails.length} guest(s) with duplicate emails were not created: ${dups}`,
        );
      } else {
        displayInfoMessage(
          `${response?.guests?.length ?? 0} guests were created`,
        );
      }
      props.onClose();
    } catch (error) {
      displayServerErrorMessage(error);
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  if (!data) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  return (
    <Paper>
      <Group justify="space-between" mb="xs" gap="0">
        <Stack gap="0">
          <Text mt="0" size="xl">
            Upload Guests
          </Text>
          <Text mt="0" size="xl">{`Event: ${props.ventName}`}</Text>
        </Stack>
      </Group>
      <Blockquote color="blue" icon={<IconInfo />} mt="xl">
        To upload guests, create a CSV file with column headers FirstName,
        LastName, and Email (case is not important). Guests must have a first
        name and an email address. No more than 250 guests can be uploaded at
        one time.
      </Blockquote>

      <FileInput
        label="CSV File"
        placeholder="Select CSV file to upload"
        accept="text/csv"
        value={selectedFile}
        onChange={(file) => {
          setSelectedFile(file);
        }}
      />

      <Flex justify="flex-end">
        <Button mt="md" mr="md" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          mt="md"
          onClick={handleUploadGuests}
          disabled={selectedFile == null || isUploading}
          rightSection={isUploading ? <Loader size={14} /> : <IconUpload />}
        >
          Upload Guests
        </Button>
      </Flex>
    </Paper>
  );
}

export default UploadGuests;
